import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import useLiveUpdates from "@/lib/useLiveUpdates";
import { RefreshCw, Edit3, Wind, Waves, Eye, Thermometer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";

export default function WeatherTidePanel({ terminalId, onUpdated }) {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    wind_speed_knots: 0,
    wind_gust_knots: 0,
    wind_direction_deg: 0,
    visibility_m: 10000,
    temperature_c: 20,
    sea_level_m: 0,
    wave_height_m: 0,
    vertical_datum: "MSL",
    valid_from: new Date().toISOString(),
    valid_to: new Date(Date.now() + 48 * 3600 * 1000).toISOString(),
    verified_by: "",
    source_reference: "",
    note: "",
  });

  const load = useCallback((refresh = false) => {
    if (!terminalId) return;
    setLoading(true);
    api.conditions(terminalId, refresh)
      .then((d) => {
        setC(d);
      })
      .catch(() => toast.error("Failed to fetch conditions"))
      .finally(() => setLoading(false));
  }, [terminalId]);

  useEffect(() => { load(false); }, [load]);

  // Auto-refresh when external 'conditions_updated' event fires
  useLiveUpdates(terminalId ? [`terminal:${terminalId}`] : [], (event) => {
    if (event === "conditions_updated") load(false);
  });

  const submitManual = async () => {
    if (!form.verified_by.trim() || !form.source_reference.trim()) {
      return toast.error("Verifier and tide source/reference are required");
    }
    try {
      await api.manualCondition({ terminal_id: terminalId, ...form });
      toast.success("Manual conditions recorded");
      setOpen(false);
      load(false);
      onUpdated?.();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  if (!terminalId) return null;
  const w = c?.weather || {};
  const m = c?.marine || {};
  const weatherSource = c?.sources?.weather_waves || {};
  const tideSource = c?.sources?.tide || {};
  const requestCount = Math.max(
    Number(weatherSource.request_count) || 0,
    Number(tideSource.request_count) || 0,
  );
  const dailyQuota = Number(tideSource.daily_quota || weatherSource.daily_quota) || null;

  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Environmental</div>
          <div className="font-heading text-sm tracking-tight">
            {c?.source === "manual" ? "Manual entry" : (c?.source_label || "Environmental forecast")}
          </div>
          {m.station?.name && <div className="text-[10px] text-slate-500 font-mono">Tide station {m.station.name} · {m.vertical_datum}</div>}
          {c && m.sea_level_m == null && <div className="text-[10px] text-amber-300">Stormglass tide unavailable · manual review required</div>}
          {m.sea_level_m != null && !m.operational_tide_verified && <div className="text-[10px] text-amber-300">Reference only · operational tide verification required</div>}
          {c?.provider_error && <div className="text-[10px] text-red-300">{c.provider_error_message || "Stormglass refresh failed · cached data requires manual review"}</div>}
        </div>
        <div className="flex gap-2">
          <Button
            data-testid={TESTIDS.btnRefreshConditions}
            size="sm"
            variant="secondary"
            disabled={loading}
            onClick={() => load(true)}
            className="rounded-none bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B]"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid={TESTIDS.btnManualConditions}
                size="sm"
                variant="secondary"
                className="rounded-none bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B]"
              >
                <Edit3 size={14} />
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0E1522] border border-[#1E293B] rounded-none text-slate-100 max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Manual weather / tide entry</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ["wind_speed_knots", "Wind (kn)"],
                  ["wind_gust_knots", "Gust (kn)"],
                  ["wind_direction_deg", "Wind dir (°)"],
                  ["visibility_m", "Visibility (m)"],
                  ["temperature_c", "Temp (°C)"],
                  ["sea_level_m", "Tide / sea level (m)"],
                  ["wave_height_m", "Wave height (m)"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs text-slate-400">{label}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: parseFloat(e.target.value) || 0 })}
                      className="bg-[#050B14] border-[#1E293B] rounded-none font-mono"
                      data-testid={`manual-input-${k}`}
                    />
                  </div>
                ))}
                <div className="col-span-2">
                  <Label className="text-xs text-slate-400">Vertical datum</Label>
                  <Input value={form.vertical_datum} onChange={(e) => setForm({ ...form, vertical_datum: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" />
                </div>
                {[["valid_from", "Valid from"], ["valid_to", "Valid to"]].map(([key, label]) => <div key={key}>
                  <Label className="text-xs text-slate-400">{label}</Label>
                  <Input type="datetime-local" value={form[key].slice(0, 16)} onChange={(e) => setForm({ ...form, [key]: new Date(e.target.value).toISOString() })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" />
                </div>)}
                <div>
                  <Label className="text-xs text-slate-400">Verified by</Label>
                  <Input value={form.verified_by} onChange={(e) => setForm({ ...form, verified_by: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Tide source / reference</Label>
                  <Input value={form.source_reference} onChange={(e) => setForm({ ...form, source_reference: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-400">Note</Label>
                  <Input
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    className="bg-[#050B14] border-[#1E293B] rounded-none"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={submitManual}
                  data-testid="btn-save-manual-condition"
                  className="bg-sky-600 hover:bg-sky-500 text-white rounded-none"
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs font-mono">
        <Stat icon={Wind} label="Wind" value={w.wind_speed_knots != null ? `${w.wind_speed_knots} kn` : "—"} sub={w.wind_gust_knots ? `gust ${w.wind_gust_knots} kn` : ""} />
        <Stat icon={Eye} label="Visibility" value={w.visibility_m != null ? `${(w.visibility_m / 1000).toFixed(1)} km` : "—"} />
        <Stat icon={Thermometer} label="Temp" value={w.temperature_c != null ? `${w.temperature_c}°C` : "—"} />
        <Stat icon={Waves} label="Tide" value={m.sea_level_m != null ? `${m.sea_level_m.toFixed?.(2) ?? m.sea_level_m} m` : "—"} sub={m.wave_height_m != null ? `wave ${m.wave_height_m}m` : ""} />
      </div>
      {c?.ts && (
        <div className="text-[10px] text-slate-500 font-mono mt-3 pt-2 border-t border-[#1E293B]">
          Updated {c.ts.slice(0, 19).replace("T", " ")}
          {c?.source !== "manual" && <span> · Forecast data, not for navigation</span>}
          {dailyQuota && <span> · Stormglass requests {requestCount}/{dailyQuota}</span>}
          {c?.sources?.tide?.url && <span> · <a className="text-sky-400 hover:text-sky-300" href={c.sources.tide.url} target="_blank" rel="noreferrer">Tide source</a></span>}
          {weatherSource.attribution && <span> · {weatherSource.attribution}</span>}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="panel-2 p-2">
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-widest">
        <Icon size={12} />
        <span>{label}</span>
      </div>
      <div className="text-slate-100 text-sm mt-0.5">{value}</div>
      {sub && <div className="text-slate-500 text-[10px]">{sub}</div>}
    </div>
  );
}
